import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type ConsumeOrganisationSequenceRequest,
  type ConsumeOrganisationSequenceResponse,
  type CreateOrganisationSequenceRequest,
  type OrganisationSequence,
  type UpdateOrganisationSequenceRequest,
} from '../client';

/**
 * Stable query-key factory for organisation-sequence caches. The list lives
 * keyed by organisation id since the BE filter is `?organisationId={id}`.
 */
export const organisationSequenceKeys = {
  all: ['directory', 'organisation-sequence'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-sequence', 'org', organisationId] as const,
};

/**
 * Fetch every numbering sequence for an organisation. Returns a flat array —
 * callers pick out the row they need by `type` (e.g. `Invoice`, `CreditNote`).
 * Pass `null` to keep the query disabled (e.g. while the org id is resolving).
 */
export function useOrganisationSequences(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<OrganisationSequence[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationSequence[], Error>({
    queryKey: organisationId
      ? organisationSequenceKeys.byOrganisation(organisationId)
      : organisationSequenceKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationSequences(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/organisationSequences`. On success
 * invalidates the per-org list so the freshly created row shows up.
 */
export function useCreateOrganisationSequence() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateOrganisationSequenceRequest>({
    mutationKey: [...organisationSequenceKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createOrganisationSequence(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({
        queryKey: organisationSequenceKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/organisationSequences`. The caller passes the
 * organisation id alongside the body so we can target the exact list-cache key
 * (the wire body itself only carries the sequence id).
 */
export function useUpdateOrganisationSequence() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateOrganisationSequenceRequest }
  >({
    mutationKey: [...organisationSequenceKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateOrganisationSequence(body),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationSequenceKeys.byOrganisation(organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around POST `/organisationSequences/consume`. Each call
 * atomically allocates and returns a fresh reference (e.g. `INV-000042`,
 * `2605-000001`) — the BE handler increments the counter on every call
 * regardless of what the caller does with the result, so cancelled
 * draft orders leave gaps in the sequence. Callers (e.g. the order
 * details screen) are expected to surface failures rather than retry.
 *
 * <p>
 * The per-org sequence list cache is invalidated on success so any open
 * Organisation page reflects the bumped counter on its next refetch.
 * </p>
 */
export function useConsumeOrganisationSequence() {
  const qc = useQueryClient();
  return useMutation<
    ConsumeOrganisationSequenceResponse,
    Error,
    ConsumeOrganisationSequenceRequest
  >({
    mutationKey: [...organisationSequenceKeys.all, 'consume'],
    mutationFn: (body) => directoryApi.consumeOrganisationSequence(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({
        queryKey: organisationSequenceKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}
